/**
 * 天気タイムライン（docs/06_ui-ux.md 2 章）。07〜21 時の時間軸で、予報の風・サーマル・雲底・雷雨リスク・日没と、
 * 自分の計画を同じ時間軸で重ねる。現在時刻を縦線で示す。
 */
import { el } from '../layout';

export interface TimelineSegment {
  kind: 'hike' | 'prep' | 'flight' | 'pack';
  fromH: number;
  toH: number;
  label: string;
}

export interface WeatherTimelineModel {
  nowH: number;
  wind: { fromH: number; toH: number; fromDeg: number; kmh: number }[];
  /** 7..20 時の 1 時間ごとのサーマルの強さ（0〜1）と上昇率 */
  thermal: { h: number; strength: number; climbMs: number }[];
  cloudBase: { h: number; m: number }[];
  storm: { fromH: number; toH: number; chance: number; region: string } | null;
  sunsetH: number;
  plan: TimelineSegment[];
}

const START_H = 7;
const END_H = 21;
const ARROWS = ['↓', '↙', '←', '↖', '↑', '↗', '→', '↘'];

/** 吹いてくる方位から、流れていく向きの矢印 */
export function windArrow(fromDeg: number): string {
  return ARROWS[Math.round((((fromDeg % 360) + 360) % 360) / 45) % 8] as string;
}

export function compass8(deg: number): string {
  return ['北', '北東', '東', '南東', '南', '南西', '西', '北西'][
    Math.round((((deg % 360) + 360) % 360) / 45) % 8
  ] as string;
}

export function createWeatherTimeline(host: HTMLElement): { render(m: WeatherTimelineModel): void } {
  host.classList.add('wtl');
  const rows = ['時刻', '風（稜線）', 'サーマル', '雲底', '雷雨', '計画'].map((name) => {
    const row = el('div', 'wtl__row');
    row.append(el('span', 'wtl__label', name), el('div', 'wtl__track'));
    host.appendChild(row);
    return row.lastElementChild as HTMLElement;
  });
  const [hoursT, windT, thermalT, cbT, stormT, planT] = rows as [
    HTMLElement,
    HTMLElement,
    HTMLElement,
    HTMLElement,
    HTMLElement,
    HTMLElement,
  ];
  const cursor = el('div', 'wtl__now');
  const trackWrap = el('div', 'wtl__cursor-layer');
  trackWrap.appendChild(cursor);
  host.appendChild(trackWrap);

  const pct = (h: number): string =>
    `${(Math.min(Math.max((h - START_H) / (END_H - START_H), 0), 1) * 100).toFixed(2)}%`;
  const span = (a: number, b: number): { left: string; width: string } => {
    const l = Math.min(Math.max((a - START_H) / (END_H - START_H), 0), 1);
    const r = Math.min(Math.max((b - START_H) / (END_H - START_H), 0), 1);
    return { left: `${(l * 100).toFixed(2)}%`, width: `${(Math.max(r - l, 0) * 100).toFixed(2)}%` };
  };
  const block = (track: HTMLElement, a: number, b: number, cls: string, text = ''): HTMLElement => {
    const d = el('div', `wtl__block ${cls}`, text);
    const s = span(a, b);
    d.style.left = s.left;
    d.style.width = s.width;
    track.appendChild(d);
    return d;
  };

  for (let h = START_H; h < END_H; h++) block(hoursT, h, h + 1, 'wtl__hour', String(h).padStart(2, '0'));

  let staticKey = '';
  return {
    render(m) {
      const key = JSON.stringify([m.wind, m.thermal, m.cloudBase, m.storm]);
      if (key !== staticKey) {
        staticKey = key;
        windT.replaceChildren();
        for (const w of m.wind)
          block(
            windT,
            w.fromH,
            w.toH,
            'wtl__wind',
            `${windArrow(w.fromDeg)} ${compass8(w.fromDeg)} ${w.kmh}`,
          );
        thermalT.replaceChildren();
        for (const t of m.thermal) {
          const b = block(
            thermalT,
            t.h,
            t.h + 1,
            'wtl__thermal',
            t.strength > 0.6 ? t.climbMs.toFixed(1) : '',
          );
          b.style.setProperty('--s', String(t.strength));
        }
        cbT.replaceChildren();
        for (let i = 0; i < m.cloudBase.length; i++) {
          const c = m.cloudBase[i] as { h: number; m: number };
          const next = m.cloudBase[i + 1]?.h ?? c.h + 2;
          block(cbT, c.h, next, 'wtl__cb', `${(c.m / 1000).toFixed(1)}k`);
        }
        stormT.replaceChildren();
        if (m.storm && m.storm.chance > 0) {
          block(
            stormT,
            m.storm.fromH,
            m.storm.toH,
            'wtl__storm',
            `${Math.round(m.storm.chance * 100)}% ${m.storm.region}`,
          );
        }
        block(stormT, m.sunsetH, END_H, 'wtl__sunset', '日没');
      }
      planT.replaceChildren();
      for (const s of m.plan) {
        const b = block(planT, s.fromH, s.toH, `wtl__plan wtl__plan--${s.kind}`, s.label);
        b.title = s.label;
      }
      cursor.style.left = pct(m.nowH);
    },
  };
}
